import { SseParser, type ChatMessage, type Event as PlayerEvent, type EventType } from '@mezha/api'
import { baseApi, TAGS, type Tag } from './baseApi'
import { http, session } from './client'
import { socialApi, type Channel } from './endpoints/social'
import { ui } from '../app/uiSlice'
import type { AppDispatch } from '../app/store'

type TagDesc = Tag | { type: Tag; id: string | number }

/** Which cached snapshots each event touches. Checked by the compiler. */
export const EVENT_TAGS: Record<EventType, (p: Record<string, unknown>) => TagDesc[]> = {
  'hero.updated': () => ['Professions', 'Hero', 'World', 'Me', 'Shop', 'Skills'],
  'hero.level_up': () => ['Hero', 'Skills'],
  'inventory.changed': () => ['Inventory', 'Shop'],
  'loadout.changed': () => ['Loadout', 'Hero', 'Inventory', 'Skills'],
  'world.arrived': () => ['World'],
  'world.encounter': () => ['World'],
  'battle.round_opened': p => battle(p),
  'battle.choice_locked': p => battle(p),
  'battle.round_resolved': p => battle(p),
  'battle.finished': p => [...battle(p), 'ActiveBattle', 'Hero', 'World', 'Story', 'Board', 'Inventory', 'Arena', 'Rating', 'Skills'],
  'quest.updated': () => ['Story', 'World', 'Evidence'],
  'saga.unlocked': () => ['Saga'],
  'board.updated': () => ['Professions', 'Board', 'World'],
  'arena.updated': () => ['Arena', 'ActiveBattle', 'Rating'],
  'chat.message': () => [],
  'clan.updated': () => ['Clans', 'Clan', 'Hero', { type: 'Chat', id: 'clan' }],
  'system.notice': () => [],
  resync: () => [...TAGS],
}

function battle(p: Record<string, unknown>): TagDesc[] {
  const id = typeof p.battle_id === 'string' ? p.battle_id : null
  return id ? [{ type: 'Battle', id }] : ['Battle']
}

const channelOf = (m: ChatMessage): Channel => m.channel as Channel

/** Applies one event to the cache. Exported for tests. */
export function applyEvent(dispatch: AppDispatch, e: PlayerEvent): void {
  const payload = (e.payload ?? {}) as Record<string, unknown>
  const tags = EVENT_TAGS[e.type]?.(payload) ?? []
  if (tags.length) dispatch(baseApi.util.invalidateTags(tags))
  if (e.type === 'chat.message') {
    const m = payload as unknown as ChatMessage
    const ch = channelOf(m)
    dispatch(
      socialApi.util.updateQueryData('chat', ch, page => {
        const i = page.messages.findIndex(x => x.id === m.id)
        if (m.hidden) {
          if (i >= 0) page.messages.splice(i, 1)
          return
        }
        if (i < 0) page.messages.unshift(m)
      }),
    )
    if (!m.hidden) dispatch(ui.actions.chatArrived({ channel: ch, message: m }))
  }
  if (e.type === 'resync') dispatch(ui.actions.notice('Обновляем сведения…'))
  if (e.type === 'arena.updated' && payload.status === 'expired') dispatch(ui.actions.notice('Заявка на арену закрылась: соперники не нашлись'))
  if (e.type === 'system.notice') {
    const text = typeof payload.text === 'string' ? payload.text : 'Объявление'
    dispatch(ui.actions.notice(text))
  }
}

const LAST_ID = 'mezha.lastEventId'
const HEARTBEAT_MS = 45_000

/** Reads the player event stream with fetch (so the bearer token is sent),
 *  keeps Last-Event-ID, reconnects with backoff and re-reads snapshots after
 *  gaps: resync, a silent connection or a tab that was in the background. */
export class EventStream {
  private abort: AbortController | null = null
  private stopped = true
  private backoff = 1000
  private lastId: string | null = null
  private hiddenAt = 0
  private watchdog: ReturnType<typeof setTimeout> | null = null

  constructor(private readonly dispatch: AppDispatch) {
    try {
      this.lastId = sessionStorage.getItem(LAST_ID)
    } catch {
      this.lastId = null
    }
  }

  start(): void {
    if (!this.stopped) return
    this.stopped = false
    document.addEventListener('visibilitychange', this.onVisibility)
    void this.loop()
  }

  stop(): void {
    this.stopped = true
    document.removeEventListener('visibilitychange', this.onVisibility)
    this.abort?.abort()
    if (this.watchdog) clearTimeout(this.watchdog)
    this.dispatch(ui.actions.connection('idle'))
  }

  private onVisibility = () => {
    if (document.visibilityState === 'hidden') this.hiddenAt = Date.now()
    else if (this.hiddenAt && Date.now() - this.hiddenAt > 30_000) {
      this.dispatch(baseApi.util.invalidateTags([...TAGS]))
      this.abort?.abort()
    }
  }

  private arm() {
    if (this.watchdog) clearTimeout(this.watchdog)
    this.watchdog = setTimeout(() => this.abort?.abort(), HEARTBEAT_MS)
  }

  private async loop(): Promise<void> {
    let lost = false
    while (!this.stopped) {
      this.abort = new AbortController()
      try {
        await this.connect(this.abort.signal, lost)
        lost = false
      } catch {
        /* reconnect below */
      }
      if (this.stopped) return
      lost = true
      this.dispatch(ui.actions.connection('lost'))
      await new Promise(r => setTimeout(r, this.backoff))
      this.backoff = Math.min(this.backoff * 2, 30_000)
    }
  }

  private async connect(signal: AbortSignal, afterGap: boolean): Promise<void> {
    if (!session.accessToken && !(await http.refresh())) throw new Error('signed out')
    const headers: Record<string, string> = { Accept: 'text/event-stream', Authorization: `Bearer ${session.accessToken}` }
    if (this.lastId) headers['Last-Event-ID'] = this.lastId
    const res = await fetch(http.url('/api/v1/events/stream'), { headers, signal, cache: 'no-store' })
    if (res.status === 401) {
      await http.refresh()
      throw new Error('unauthorized')
    }
    if (!res.ok || !res.body) throw new Error(`stream ${res.status}`)
    this.backoff = 1000
    this.dispatch(ui.actions.connection('live'))
    if (afterGap) this.dispatch(baseApi.util.invalidateTags([...TAGS]))
    this.arm()
    const reader = res.body.pipeThrough(new TextDecoderStream()).getReader()
    const parser = new SseParser()
    for (;;) {
      const { value, done } = await reader.read()
      if (done) throw new Error('stream closed')
      this.arm()
      for (const msg of parser.push(value)) {
        if (!msg.data) continue
        let e: PlayerEvent
        try {
          e = JSON.parse(msg.data) as PlayerEvent
        } catch {
          continue
        }
        if (msg.id) {
          this.lastId = msg.id
          try {
            sessionStorage.setItem(LAST_ID, msg.id)
          } catch {
            /* ignore */
          }
        }
        applyEvent(this.dispatch, e)
      }
    }
  }
}
